import { useEffect, useState } from "react";
import { loadPeople, type People } from "../lib/org";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Pick email recipients from the company's users and saved contacts, or type any other address.
 * Value is a plain list of lower-case emails.
 */
export default function RecipientPicker({ org, value, onChange, placeholder = "name@company.com", disabled }: {
  org: string | null | undefined; value: string[]; onChange: (v: string[]) => void; placeholder?: string; disabled?: boolean;
}) {
  const [people, setPeople] = useState<People | null>(null);
  const [typed, setTyped] = useState("");
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { if (org) loadPeople(org).then(setPeople).catch(() => setPeople({ users: [], contacts: [] })); }, [org]);

  const options = [
    ...(people?.users ?? []).filter((u) => u.email).map((u) => ({ email: u.email!.toLowerCase(), label: u.name || u.email!, group: "Users" })),
    ...(people?.contacts ?? []).filter((c) => c.email).map((c) => ({ email: c.email!.toLowerCase(), label: `${c.name}${c.role ? ` (${c.role})` : ""}`, group: "Contacts" })),
  ];
  const labelOf = (e: string) => options.find((o) => o.email === e)?.label;
  const add = (e: string) => {
    const v = e.trim().toLowerCase();
    if (!v) return;
    if (!EMAIL.test(v)) { setErr("That doesn't look like an email address"); return; }
    setErr(null);
    if (!value.includes(v)) onChange([...value, v]);
    setTyped("");
  };
  const free = options.filter((o) => !value.includes(o.email));

  return (
    <div className={`rpick ${disabled ? "disabled" : ""}`}>
      <div className="rpick-chips">
        {value.map((e) => (
          <span key={e} className="rchip" title={e}>
            {labelOf(e) ? <><b>{labelOf(e)}</b><small>{e}</small></> : <b>{e}</b>}
            {!disabled && <button type="button" aria-label={`Remove ${e}`} onClick={() => onChange(value.filter((x) => x !== e))}>×</button>}
          </span>
        ))}
        {!value.length && <span className="sub">No recipients yet.</span>}
      </div>
      {!disabled && (
        <div className="rpick-add">
          {free.length > 0 && (
            <select value="" onChange={(e) => e.target.value && add(e.target.value)} aria-label="Add a company user or contact">
              <option value="">+ Add user or contact…</option>
              {["Users", "Contacts"].map((g) => free.some((o) => o.group === g) && (
                <optgroup key={g} label={g}>
                  {free.filter((o) => o.group === g).map((o) => <option key={o.email} value={o.email}>{o.label} — {o.email}</option>)}
                </optgroup>
              ))}
            </select>
          )}
          <input type="email" value={typed} placeholder={placeholder} onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(typed); } }} />
          <button type="button" className="btn-ghost small" onClick={() => add(typed)} disabled={!typed.trim()}>Add</button>
        </div>
      )}
      {err && <div className="warn-text sub">{err}</div>}
    </div>
  );
}
